import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  HostBinding
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  IdService,
  DataAnalyticsTaggingConstants,
  DataAnalyticsTaggingEvent,
  ToastMessageService
} from '@tdwb/core';

import { LearnRestService, Lesson, RouterService } from '@tdwb/webbroker/core';

@Component({
  selector: 'td-wb-learn-saved-video',
  templateUrl: './learn-saved-video.component.html',
  styleUrls: ['./learn-saved-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LearnSavedVideoComponent implements OnDestroy {
  @Input() lesson: Lesson;
  @Input() sourceName: string;
  @Output() readonly bookmarkChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly lessonSelected: EventEmitter<Lesson> = new EventEmitter<Lesson>();
  @HostBinding('style.width.px')
  @Input() width: number;

  uniqueId: string;
  bookmarkUniqueId: string;
  saveDisabled: boolean = false;
  readonly destroy: Subject<void> = new Subject<void>();
  readonly aspectRatio: string = '16:9';
  eventData: DataAnalyticsTaggingEvent = {};

  constructor(private learnRestService: LearnRestService,
              private toastMessageService: ToastMessageService,
              private changeDetectorRef: ChangeDetectorRef,
              private routerService: RouterService,
              private idService: IdService) {
    this.generateUniqueId();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  toggleSave(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.saveDisabled = true;
    this.learnRestService.toggleVideoBookMark(this.lesson)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (): void => {
          this.lesson.saved = !this.lesson.saved;
          this.bookmarkChange.emit(this.lesson.saved);
          this.saveDisabled = false;
          const messageKey = this.lesson.saved ? 'tdWb.learn.notification.SAVED' : 'tdWb.learn.notification.UNSAVED';
          const action = this.lesson.saved ? 'saved' : 'unsaved';
          this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:${this.getVideoSection()}:${action}:${this.lesson.title}`);
          this.toastMessageService.setToast(messageKey);
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.saveDisabled = false;
        }
      });
  }

  getVideoSection(): string {
    let videoSection;
    switch(this.sourceName){
      case 'recommendation':
        videoSection = 'Previously Watched';
        break;
      case 'most-liked':
        videoSection = 'Most Popular';
        break;
      case 'new-account':
        videoSection = 'New Here';
        break;
      case 'selected-topics':
        videoSection = 'Selected Topics';
        break;
    }
    return videoSection;
  }

  selectLesson(): void {
    const videoSection = this.getVideoSection();
    this.eventData = DataAnalyticsTaggingConstants.getVideoPageEventData('lesson', videoSection, this.lesson.title);
    if (this.sourceName === 'related-lessons') {
      this.lessonSelected.emit(this.lesson);
    } else {
      this.routerService.go('page.learn.lesson', { id: this.lesson.uniqueKey, section: videoSection });
    }
  }

  private generateUniqueId(): void {
    const prefix = this.sourceName ?
      'td-wb-learn-saved-video-lc-' + this.sourceName + '-card' :
      'td-wb-learn-saved-video-lc-card';
    this.uniqueId = this.idService.generateNamespaceSequentialId(prefix);
    const bookmarkPrefix = this.sourceName ? 'td-wb-learn-saved-video-lc-' + this.sourceName + '-bookmark' :
      'td-wb-learn-saved-video-save';
    this.bookmarkUniqueId = this.idService.generateNamespaceSequentialId(bookmarkPrefix);
  }
}
